import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../common/Icon/Icon';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import { adminFetch, shortDate } from './adminUtil';

/**
 * Middleman shops: who's active, paid through when, and the two levers
 * (record a month's payment, suspend). New shops start from Look up
 * ("Make shop" on a person) or the search here.
 */
function AdminShops({ onChanged, go }) {
    const [shops, setShops] = useState(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState(null);
    const [suspendFor, setSuspendFor] = useState(null);

    const load = useCallback(async () => {
        try { setShops((await adminFetch('/api/shops/admin/list')).shops || []); }
        catch (e) { setMsg(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (query.trim().length < 2) { setResults([]); return undefined; }
        const t = setTimeout(async () => {
            try { setResults((await adminFetch(`/api/shops/admin/users?q=${encodeURIComponent(query.trim())}`)).users || []); }
            catch { setResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [query]);

    const act = async (key, url, body, done) => {
        setBusy(key); setMsg(null);
        try {
            await adminFetch(url, { method: 'POST', body: body || {} });
            setMsg(done);
            await load();
            onChanged();
        } catch (e) { setMsg(e.message); }
        finally { setBusy(null); setSuspendFor(null); }
    };

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const lapsed = (s) => s.status === 'active' && (!s.paid_until || new Date(s.paid_until) < today);

    return (
        <div className="adm-shops">
            <ConfirmDialog
                open={!!suspendFor}
                title={`Suspend ${suspendFor?.name}?`}
                confirmLabel="Suspend"
                danger
                busy={!!busy}
                message="It stops being offered as a middleman for new trades. Packages it already holds show up on the overview. Recording a payment switches it back on."
                onCancel={() => setSuspendFor(null)}
                onConfirm={() => act(`s${suspendFor.id}`, `/api/shops/admin/${suspendFor.id}/suspend`, null, `${suspendFor.name} is suspended.`)}
            />
            {msg && <p className="adm-msg">{msg}</p>}

            {!shops ? <p className="adm-loading">Loading shops&hellip;</p> : shops.length === 0 ? (
                <div className="adm-empty"><Icon name="store" size={28} /> No shops yet.</div>
            ) : (
                <ul className="adm-shop-list">
                    {shops.map(s => (
                        <li key={s.id} className={`adm-shop ${s.status}${lapsed(s) ? ' lapsed' : ''}`}>
                            <div className="adm-shop-main">
                                <strong>{s.name}</strong>
                                <span className={`adm-chip ${s.status === 'active' ? (lapsed(s) ? 'warn' : 'ok') : s.status === 'suspended' ? 'bad' : ''}`}>
                                    {lapsed(s) ? 'active, fee lapsed' : s.status.replace('_', ' ')}
                                </span>
                            </div>
                            <p className="adm-shop-meta">
                                <button type="button" className="adm-link" onClick={() => go('lookup', { user: s.user_id })}>{s.owner_username}</button>
                                {' '}· ${Number(s.monthly_fee).toFixed(0)} a month
                                {' '}· {s.paid_until ? `paid through ${shortDate(s.paid_until)}` : 'never paid'}
                            </p>
                            <div className="adm-actions">
                                <button type="button" className="adm-btn primary" disabled={busy === `p${s.id}`}
                                    onClick={() => act(`p${s.id}`, `/api/shops/admin/${s.id}/mark-paid`, { months: 1 }, `${s.name}: one more month recorded.`)}>
                                    +1 month paid
                                </button>
                                {s.status !== 'suspended' && (
                                    <button type="button" className="adm-btn danger" onClick={() => setSuspendFor(s)}>Suspend</button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <section className="adm-block">
                <h3 className="adm-h3">Make someone a shop</h3>
                <label className="adm-search small">
                    <Icon name="search" size={16} />
                    <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users by name or email" />
                </label>
                {results.length > 0 && (
                    <ul className="adm-rows">
                        {results.map(u => (
                            <li key={u.id} className="adm-row static">
                                <span className="adm-row-main"><strong>{u.username}</strong> <span className="adm-muted">{u.email}</span></span>
                                {u.role === 'user' ? (
                                    <button type="button" className="adm-btn primary small" disabled={busy === `g${u.id}`}
                                        onClick={() => act(`g${u.id}`, '/api/shops/admin/grant', { userId: u.id }, `${u.username} now has a shop, waiting for its first payment.`)}>
                                        Make shop
                                    </button>
                                ) : <span className={`adm-chip role-${u.role}`}>{u.role}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

export default AdminShops;
